import { Outlet, useParams, Link } from "react-router-dom"
import { ChevronRight, Home } from "lucide-react"
import PublicNavbar from "@/components/shared/PublicNavbar"
import PublicFooter from "@/components/shared/PublicFooter"
import { getToolBySlug, TOOL_CATEGORIES } from "./toolRegistry"
import CategorySidebar from "./components/CategorySidebar"
import CategoryTabs from "./components/CategoryTabs"
import { useEffect, useState } from "react"
import { APP_NAME } from "@/lib/appConfig"
import { SEO } from "@/components/SEO"
export default function ToolPageLayout() {
  const { slug } = useParams()
  const tool = slug ? getToolBySlug(slug) : null

  const [currency, setCurrency] = useState(() => {
    return localStorage.getItem('rentbase_currency_override') || '€'
  })

  // Auto-detect currency for first-time visitors
  useEffect(() => {
    const saved = localStorage.getItem('rentbase_currency_override')
    if (!saved) {
      fetch("https://get.geojs.io/v1/ip/country.json")
        .then(res => res.json())
        .then(data => {
          if (!data || !data.country) return;
          const code = data.country;
          let detected = '€'; // Default to Euro for EU and fallbacks

          if (['US', 'PR', 'EC', 'SV'].includes(code)) detected = '$';
          else if (['GB', 'IM', 'JE', 'GG'].includes(code)) detected = '£';
          else if (['CA'].includes(code)) detected = 'C$';
          else if (['AU', 'NR', 'TV', 'KI'].includes(code)) detected = 'A$';
          else if (['NZ', 'CK', 'NU', 'PN', 'TK'].includes(code)) detected = 'NZ$';
          else if (['IN', 'BT'].includes(code)) detected = '₹';
          else if (['JP'].includes(code)) detected = '¥';
          else if (['CN'].includes(code)) detected = '¥'; // Same symbol for Yen/Yuan in our group
          else if (['ZA', 'LS', 'NA'].includes(code)) detected = 'R';
          else if (['AE'].includes(code)) detected = 'د.إ';
          else if (['NG'].includes(code)) detected = '₦';
          else if (['RU'].includes(code)) detected = '₽';
          else if (['CH', 'LI'].includes(code)) detected = 'CHF';
          else if (['BR'].includes(code)) detected = 'R$';
          else if (['MX'].includes(code)) detected = 'Mex$';
          else if (['KR'].includes(code)) detected = '₩';
          else if (['SG'].includes(code)) detected = 'S$';
          else if (['DK', 'NO', 'SE', 'IS'].includes(code)) detected = 'kr';

          setCurrency(detected);
          localStorage.setItem('rentbase_currency_override', detected);
        })
        .catch(err => console.error("Currency auto-detect failed", err));
    }
  }, []);

  // Save manual overrides
  useEffect(() => {
    localStorage.setItem('rentbase_currency_override', currency)
  }, [currency])

  useEffect(() => {
    if (!tool) return

    document.title = `${tool.title} — Free Tool | ${APP_NAME}`

    const setMeta = (key: string, content: string, attr = "name") => {
      let el = document.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null
      if (!el) {
        el = document.createElement("meta")
        el.setAttribute(attr, key)
        document.head.appendChild(el)
      }
      el.setAttribute("content", content)
    }

    const descContent = `${tool.description} Free, instant, no sign-up needed.`
    setMeta("description", descContent)
    setMeta("og:title", `${tool.title} | ${APP_NAME}`, "property")
    setMeta("og:description", descContent, "property")
    setMeta("og:type", "website", "property")
    setMeta("og:url", `https://rent-keep.com/tools/${tool.slug}`, "property")

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null
    if (!canonical) {
      canonical = document.createElement("link")
      canonical.setAttribute("rel", "canonical")
      document.head.appendChild(canonical)
    }
    canonical.setAttribute("href", `https://rent-keep.com/tools/${tool.slug}`)

    // BreadcrumbList JSON-LD
    const catLabel = TOOL_CATEGORIES[tool.category]?.label || "Tools"
    const breadcrumbScript = document.createElement("script")
    breadcrumbScript.type = "application/ld+json"
    breadcrumbScript.id = "breadcrumb-schema"
    breadcrumbScript.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://rent-keep.com" },
        { "@type": "ListItem", position: 2, name: "Free Tools", item: "https://rent-keep.com/tools" },
        { "@type": "ListItem", position: 3, name: catLabel, item: `https://rent-keep.com/tools#${tool.category}` },
        { "@type": "ListItem", position: 4, name: tool.title },
      ],
    })
    document.head.querySelector("#breadcrumb-schema")?.remove()
    document.head.appendChild(breadcrumbScript)

    return () => {
      document.head.querySelector("#breadcrumb-schema")?.remove()
    }
  }, [tool])

  if (!tool) {
    // If not found, maybe just render Outlet which might be a 404
    return (
      <div className="min-h-screen flex flex-col bg-slate-50 pt-16">
        <PublicNavbar />
        <main className="flex-1 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Outlet />
        </main>
        <PublicFooter />
      </div>
    )
  }

  const categoryName = TOOL_CATEGORIES[tool.category]?.label || "Category"

  return (
    <div className="min-h-screen flex flex-col bg-slate-50 font-sans pt-16">
      <SEO 
        title={`${tool.title} | ${APP_NAME} Free Tools`}
        description={tool.description}
        canonicalUrl={`https://rent-keep.com/tools/${tool.slug}`}
        jsonLd={{
          "@type": "WebApplication",
          "name": tool.title,
          "description": tool.description,
          "applicationCategory": "BusinessApplication",
          "operatingSystem": "Web",
          "url": `https://rent-keep.com/tools/${tool.slug}`
        }}
      />
      <PublicNavbar />

      {/* Breadcrumb row */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <nav className="flex items-center text-sm font-medium text-slate-500 overflow-x-auto whitespace-nowrap scrollbar-hide">
            <Link to="/" className="hover:text-primary transition-colors flex items-center">
              <Home className="w-4 h-4 mr-1" />
              Home
            </Link>
            <ChevronRight className="w-4 h-4 mx-2 flex-shrink-0 text-slate-400" />
            <Link to="/tools" className="hover:text-primary transition-colors">
              Tools
            </Link>
            <ChevronRight className="w-4 h-4 mx-2 flex-shrink-0 text-slate-400" />
            <span className="text-slate-600">{categoryName}</span>
            <ChevronRight className="w-4 h-4 mx-2 flex-shrink-0 text-slate-400" />
            <span className="text-slate-900 truncate" aria-current="page">
              {tool.shortTitle}
            </span>
          </nav>

          <div className="flex-shrink-0 self-start sm:self-auto flex flex-col items-start sm:items-end gap-1.5">
            <div className="flex items-center bg-slate-50 rounded-lg border border-slate-200 shadow-sm relative overflow-hidden transition-all hover:border-slate-300">
              <label htmlFor="currency-override" className="text-xs font-semibold text-slate-500 uppercase tracking-widest pl-3 pr-2 py-2 border-r border-slate-200 bg-slate-100/50">
                Currency
              </label>
              <select
                id="currency-override"
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                className="text-sm font-medium border-0 focus:ring-0 text-slate-800 bg-transparent cursor-pointer pl-3 pr-8 py-2 outline-none hover:text-primary transition-colors appearance-none min-w-[140px]"
                title="Select Regional Currency"
              >
                <optgroup label="Popular">
                  <option value="€">€ EUR - Euro</option>
                  <option value="$">$ USD - US Dollar</option>
                  <option value="£">£ GBP - British Pound</option>
                </optgroup>
                <optgroup label="Americas">
                  <option value="C$">C$ CAD - Canadian Dollar</option>
                  <option value="R$">R$ BRL - Brazilian Real</option>
                  <option value="Mex$">Mex$ MXN - Mexican Peso</option>
                </optgroup>
                <optgroup label="Europe">
                  <option value="CHF">CHF - Swiss Franc</option>
                  <option value="kr">kr - Scand. Krone</option>
                  <option value="₽">₽ RUB - Russian Ruble</option>
                </optgroup>
                <optgroup label="Asia Pacific">
                  <option value="A$">A$ AUD - Australian Dollar</option>
                  <option value="NZ$">NZ$ NZD - New Zealand Dollar</option>
                  <option value="¥">¥ CNY/JPY - Yuan/Yen</option>
                  <option value="₹">₹ INR - Indian Rupee</option>
                  <option value="₩">₩ KRW - South Korean Won</option>
                  <option value="S$">S$ SGD - Singapore Dollar</option>
                </optgroup>
                <optgroup label="Middle East & Africa">
                  <option value="د.إ">د.إ AED - UAE Dirham</option>
                  <option value="R">R ZAR - South African Rand</option>
                  <option value="₦">₦ NGN - Nigerian Naira</option>
                </optgroup>
              </select>
              <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
              </div>
            </div>
            <span className="text-[10px] text-slate-400 font-medium px-1 truncate max-w-[400px]">
              Seelct Currency as per your preference. Selection here applies to all tools.
            </span>
          </div>
        </div>
      </div>

      {/* Main Layout */}
      <div className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 w-full flex flex-col md:flex-row gap-8">

        {/* Desktop Sidebar */}
        <div className="hidden md:block w-64 flex-shrink-0">
          <div className="sticky top-24">
            <CategorySidebar activeSlug={tool.slug} />
          </div>
        </div>

        {/* Mobile Tabs */}
        <div className="block md:hidden border-b border-slate-200 -mx-4 px-4 pb-2 mb-4">
          <CategoryTabs activeCategory={tool.category} />
        </div>

        {/* Content Area */}
        <main className="flex-1 min-w-0 flex flex-col">
          <Outlet context={{ tool, currency }} />

          {/* Global Legal Disclaimer */}
          <div className="mt-12 mb-4 pt-6 border-t border-slate-200">
            <p className="text-xs text-slate-400 text-center mx-auto max-w-4xl leading-relaxed">
              <strong>Disclaimer:</strong> These calculators are provided for preliminary educational estimations and screening purposes only.
              Actual real estate figures may vary significantly based on shifting local legislation, specific regional tax requirements, dynamic interest rates, and volatile market conditions.
              Always consult with a certified financial advisor, tax specialist, or legal professional before executing any binding investment decisions or assuming financial risk.
            </p>
          </div>
        </main>

      </div>

      <PublicFooter />
    </div>
  )
}
